import React from 'react';
import { Authenticator } from '@aws-amplify/ui-react';
import logoSymbol from '@/assets/logo_transparent.png';

interface LandingPageProps {
	activeTab: 'home' | 'about' | 'terms' | 'privacy' | 'login';
	setActiveTab: (tab: 'home' | 'about' | 'terms' | 'privacy' | 'login') => void;
}

export default function LandingPage({ activeTab, setActiveTab }: LandingPageProps) {
	return (
		<div className="xnav-landing-container">
			{/* Top Navbar */}
			<div className="xnav-landing-nav">
				<div className="xnav-nav-left">
					<img src={logoSymbol} alt="XNAV Logo" style={{ width: '38px', height: '38px', objectFit: 'contain', marginRight: '10px', filter: 'drop-shadow(0 2px 8px rgba(14, 165, 233, 0.45))' }} />
					<span className="xnav-nav-brand">
						XN<span className="xnav-brand-glow-a" aria-label="A">Λ</span>V
					</span>
					<div className="xnav-nav-links-group">
						<button 
							className={`xnav-nav-link ${activeTab === 'home' ? 'active' : ''}`} 
							onClick={() => setActiveTab('home')}
						>
							Home
						</button>
						<button 
							className={`xnav-nav-link ${activeTab === 'about' ? 'active' : ''}`} 
							onClick={() => setActiveTab('about')}
						>
							About Us
						</button>
						<button 
							className={`xnav-nav-link ${activeTab === 'terms' ? 'active' : ''}`} 
							onClick={() => setActiveTab('terms')}
						>
							Terms
						</button>
						<button 
							className={`xnav-nav-link ${activeTab === 'privacy' ? 'active' : ''}`} 
							onClick={() => setActiveTab('privacy')}
						>
							Privacy
						</button>
					</div>
				</div>
				<div className="xnav-nav-right">
					{activeTab !== 'home' && activeTab !== 'login' && (
						<button 
							className={`xnav-login-action-btn ${activeTab === 'login' ? 'active' : ''}`} 
							onClick={() => setActiveTab('login')}
						>
							Login
						</button>
					)}
				</div>
			</div>

			{/* Main Content Area */}
			<div className="xnav-landing-content">
				{activeTab === 'home' && (
					<div className="xnav-hero-section">
						<img src={logoSymbol} alt="XNav Logo" style={{ width: '96px', height: '96px', objectFit: 'contain', margin: '0 auto 1.5rem', display: 'block', filter: 'drop-shadow(0 8px 24px rgba(14, 165, 233, 0.45))' }} />
						<div className="xnav-hero-company-badge">by 7D Imaging</div>
						<h1 className="xnav-hero-title">XN<span className="xnav-brand-glow-a" aria-label="A">Λ</span>V</h1>
						<p className="xnav-hero-subtitle">The Universal Learning & Training Ecosystem</p>
						<p className="xnav-hero-description">
							XNav is an advanced educational ecosystem designed to host interactive simulators, video annotations, step-by-step procedures, and structured learning pathways. From medical sciences and artificial intelligence to business operations and marketing, XNav provides a highly customizable training environment to help teams, students, and professionals master any complex subject matter.
						</p>
						<button className="xnav-hero-cta-btn" onClick={() => setActiveTab('login')}>
							Launch XNav Studio
						</button>
					</div>
				)}

				{activeTab === 'about' && (
					<div className="xnav-about-section">
						<h2 className="xnav-section-title">About Us</h2>
						<div className="xnav-about-grid">
							<div className="xnav-about-card">
								<h3>7D Imaging, Inc.</h3>
								<p>
									Established in 2012 by Dr. Cliff Gronseth, 7D Imaging is a pioneer in interactive training software. XNav, our flagship learning platform, provides state-of-the-art course-building modules, hands-on simulators, and visual testing engines built to help students and professionals master complex workflows across various clinical, technical, and academic domains.
								</p>
								<p style={{ marginTop: '1rem' }}>
									Learn more about our educational technology at <a href="https://7d-imaging.com/" target="_blank" rel="noopener noreferrer" style={{ color: '#0ea5e9', textDecoration: 'underline' }}>7D-Imaging.com</a>.
								</p>
							</div>
							<div className="xnav-about-card">
								<h3>Dr. Cliff Gronseth, MD</h3>
								<h4 style={{ color: '#38bdf8', fontSize: '1rem', fontWeight: '800', marginBottom: '1rem', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
									Founder & CEO of XNav & 7D Imaging
								</h4>
								<p>
									Dr. Gronseth is the Founder and CEO of XNav and 7D Imaging, and the Founder of Spine West in Boulder, CO. He is a triple board-certified physician specializing in physical medicine & rehabilitation, electrodiagnostics, and pain medicine.
								</p>
								<p style={{ marginTop: '1rem' }}>
									In 2015, Dr. Gronseth founded the NYAGI Project, a global humanitarian nonprofit. As a physician, educator, and humanitarian, Dr. Gronseth designed XNav to democratize high-fidelity educational tools, enabling instructors to deliver step-by-step masterclasses and interactive testing worldwide.
								</p>
							</div>
						</div>
					</div>
				)}

				{activeTab === 'terms' && (
					<div className="xnav-simple-section">
						<h2 className="xnav-section-title">Terms of Service</h2>
						<div className="xnav-simple-card">
							<p>
								Welcome to XNav. By accessing our platform, you agree to comply with our terms. All educational content, media scanning streams, and course templates are owned by 7D Imaging. Unauthorized distribution, copying, or replication of proprietary source files is strictly prohibited under intellectual property laws.
							</p>
							<p style={{ marginTop: '1.5rem', color: '#64748b' }}>
								Terms of Service updates are coming soon. For inquiries, contact info@7d-imaging.com.
							</p>
						</div>
					</div>
				)}

				{activeTab === 'privacy' && (
					<div className="xnav-simple-section">
						<h2 className="xnav-section-title">Privacy Policy</h2>
						<div className="xnav-simple-card">
							<p>
								Your privacy is our priority. XNav collects account registration details, quiz completion records, and activity logs to optimize learning pathways. We do not share personal credentials or student analytics with third-party advertising companies.
							</p>
							<p style={{ marginTop: '1.5rem', color: '#64748b' }}>
								Privacy policy details are coming soon.
							</p>
						</div>
					</div>
				)}

				{activeTab === 'login' && (
					<div className="xnav-login-form-wrapper">
						<Authenticator 
							socialProviders={['google']} 
							components={{
								Header() {
									return (
										<div className="xnav-login-header">
											<img src={logoSymbol} alt="XNav Logo" style={{ width: '56px', height: '56px', objectFit: 'contain', margin: '0 auto 1rem', display: 'block', filter: 'drop-shadow(0 4px 14px rgba(14, 165, 233, 0.4))' }} />
											<div className="xnav-company-badge">by 7D Imaging</div>
											<h1 className="xnav-logo-text">XNav</h1>
											<p className="xnav-subtitle">Next-Generation Learning & Training Platform</p>
										</div>
									);
								}
							}}
						/>
					</div>
				)}
			</div>
		</div>
	);
}
